/* eslint-disable no-unused-vars */
import React, { useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faXmark, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { uploadSingleFile } from "../../../api/users";
import PhotoUploadTray from "../../common/PhotoUploadTray";

const CATEGORIES = [
  { key: "headshots", label: "Headshots", mobileLabel: "Headshots" },
  { key: "media", label: "Production Stills", mobileLabel: "Stills" },
  { key: "behind", label: "Behind the Scenes", mobileLabel: "BTS" },
  { key: "premieres", label: "Premieres", mobileLabel: "Premieres" },
];

export default function UserPhotoGallery({ data, isEditMode, onChange, onMarkMediaForDeletion }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  
  const [activeCategory, setActiveCategory] = useState("headshots");
  const sliderRef = useRef(null);

  const [expandedIndex, setExpandedIndex] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const albums = useMemo(() => {
    const pa = data?.photo_albums || {};
    return {
      headshots: pa.headshots || [],
      media: pa.media || [],
      behind: pa.behind || [],
      premieres: pa.premieres || [],
    };
  }, [data]);

  const currentCategoryImages = albums[activeCategory] || [];

  const handleScroll = (dir) => {
    if (sliderRef.current) {
      const scrollAmount = 256 + 32; 
      sliderRef.current.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  // A batch settles seconds after Upload is pressed, so read the newest data here.
  // Reading the prop captured at click time would resurrect a photo deleted meanwhile,
  // while its S3 key stays queued for deletion: a permanently broken image after save.
  const dataRef = useRef(data);
  dataRef.current = data;

  const handleUploaded = (keys) => {
    const latest = dataRef.current?.photo_albums;
    const newPhotoAlbums = {
      headshots: [...(latest?.headshots || [])],
      media: [...(latest?.media || [])],
      behind: [...(latest?.behind || [])],
      premieres: [...(latest?.premieres || [])],
    };

    const newEntries = keys.map((k) =>
      activeCategory === "headshots"
        ? { image: k, isMain: false }
        : { image: k }
    );

    newPhotoAlbums[activeCategory].push(...newEntries);

    onChange("photo_albums", newPhotoAlbums);
    setTimeout(() => handleScroll("right"), 300);
  };

  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    // The server would keep the file anyway (picture still uses it), so block
    // the delete here instead of silently dropping it from the gallery only.
    if (item.image && item.image === data?.picture) {
      toast.error(t("This photo is your current profile picture and cannot be deleted. Change your profile picture first."));
      return;
    }
    setPhotoToDelete(item);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    const newAlbums = {
      headshots: [...(data?.photo_albums?.headshots || [])],
      media: [...(data?.photo_albums?.media || [])],
      behind: [...(data?.photo_albums?.behind || [])],
      premieres: [...(data?.photo_albums?.premieres || [])]
    };

    newAlbums[activeCategory] = (newAlbums[activeCategory] || []).filter(img => img.image !== photoToDelete.image);
    onChange("photo_albums", newAlbums);
    if (onMarkMediaForDeletion) onMarkMediaForDeletion(photoToDelete.image);
    setDeleteModalOpen(false);
    setPhotoToDelete(null);
  };

  return (
    <section className="tw-bg-transparent tw-w-full tw-py-12 tw-border-[#5A3F49]/30">
      <div className="tw-w-full tw-max-w-[1280px] tw-mx-auto tw-px-4 md:tw-px-0">
        
        <h2 className="tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-8">
          {t("Portfolio")}
        </h2>

        <div className="tw-pl-0 md:tw-pl-10">
          <div className="tw-bg-[#280D41] tw-border tw-border-[#5A3F49]/40 tw-rounded-[24px] tw-p-6 md:tw-p-10 tw-flex tw-flex-col tw-gap-8">
            
            <div className="tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center tw-justify-between tw-gap-6">
              <h3 className="tw-text-white tw-text-2xl md:tw-text-3xl tw-font-bold tw-m-0">
                {t("Photo Albums")}
              </h3>
              
              <div className="tw-flex tw-gap-2 tw-overflow-x-auto custom-scrollbar tw-pb-2 lg:tw-pb-0">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => setActiveCategory(cat.key)}
                    className={`tw-whitespace-nowrap tw-px-5 tw-py-2 tw-rounded-full tw-font-bold tw-text-xs tw-transition-all tw-border-none tw-cursor-pointer ${
                      activeCategory === cat.key
                        ? "tw-bg-[#FF43A7] tw-text-[#570033] shadow-lg"
                        : "tw-bg-[#371E51] tw-text-[#F0DBFF] hover:tw-bg-[#4B2B6D]"
                    }`}
                  >
                    {t(cat.label)}
                  </button>
                ))}
              </div>
            </div>

            {/* Slider Area */}
            <div className="tw-relative">
              <div 
                ref={sliderRef}
                className="tw-flex tw-items-stretch tw-gap-4 md:tw-gap-6 tw-overflow-x-auto tw-snap-x tw-pb-4 custom-scrollbar"
              >
                {isEditMode && (
                  <PhotoUploadTray
                    addLabel={`${t("Add")} ${t(CATEGORIES.find((c) => c.key === activeCategory).mobileLabel)}`}
                    uploadFn={uploadSingleFile}
                    token={user?.token}
                    max={10}
                    resetKey={activeCategory}
                    tileClass="tw-w-[140px] md:tw-w-[240px] tw-h-[210px] md:tw-h-[360px]"
                    onUploaded={handleUploaded}
                    onError={(msg) => {
                      setErrorMessage(msg);
                      setErrorModalOpen(true);
                    }}
                  />
                )}

                {currentCategoryImages.map((item, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => !isEditMode && setExpandedIndex(idx)}
                    className="tw-shrink-0 tw-snap-center tw-relative tw-w-[140px] md:tw-w-[240px] tw-h-[210px] md:tw-h-[360px] tw-bg-[#1F0439] tw-rounded-xl tw-overflow-hidden tw-group tw-cursor-pointer tw-shadow-lg"
                   >
                      <img 
                       src={item.image?.startsWith('http') ? item.image : `${process.env.REACT_APP_AWS_URL}/${item.image}`} 
                        alt="Gallery" 
                        className="tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-105 tw-transition-transform tw-duration-500"
                      />
                    
                    {isEditMode && (
                      <div className="tw-absolute tw-top-3 tw-right-3 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                        <button 
                          onClick={(e) => handleDeleteClick(e, item)}
                          className="tw-w-8 tw-h-8 tw-bg-black/70 hover:tw-bg-red-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-border-none tw-cursor-pointer"
                        >
                          <FontAwesomeIcon icon={faTrashCan} className="tw-text-sm" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LIGHTBOX MODAL */}
      {expandedIndex !== null && (
        <div 
          className="tw-fixed tw-inset-0 tw-z-[9999] tw-bg-[#0a0014]/95 tw-backdrop-blur-md tw-flex tw-items-center tw-justify-center tw-overflow-hidden"
          onClick={() => setExpandedIndex(null)}
        >
          {/* MOBILE ONLY ARROWS: Pinned to viewport edges */}
          {currentCategoryImages.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); setExpandedIndex((prev) => (prev - 1 + currentCategoryImages.length) % currentCategoryImages.length); }}
                className="md:tw-hidden tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-z-[130] tw-text-white tw-bg-transparent tw-border-none tw-drop-shadow-[0_2px_8px_rgba(0,0,0,1)]"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="tw-text-4xl" />
              </button>

              <button 
                onClick={(e) => { e.stopPropagation(); setExpandedIndex((prev) => (prev + 1) % currentCategoryImages.length); }}
                className="md:tw-hidden tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-z-[130] tw-text-white tw-bg-transparent tw-border-none tw-drop-shadow-[0_2px_8px_rgba(0,0,0,1)]"
              >
                <FontAwesomeIcon icon={faChevronRight} className="tw-text-4xl" />
              </button>
            </>
          )}

          {/* Image & Desktop Navigation Container */}
          <div 
            className="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center" 
            onClick={(e) => e.stopPropagation()} 
          >
            <div className="tw-relative">
              
              {/* The X-Mark Button: Mobile Fixed to Corner | Desktop Absolute above Arrow */}
              <button 
                className="tw-fixed tw-top-6 tw-right-6 md:tw-absolute md:tw-top-0 md:tw--right-24 tw-w-12 tw-h-12 tw-z-[120] tw-flex tw-items-center tw-justify-center 
                 tw-bg-white/10 tw-backdrop-blur-md tw-border tw-border-white/20 tw-rounded-full tw-text-white 
                 tw-drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] hover:tw-text-[#FF00A0] hover:tw-bg-white/20 
                 tw-transition-all tw-cursor-pointer"
                onClick={() => setExpandedIndex(null)}
              >
                <FontAwesomeIcon icon={faXmark} className="tw-text-2xl" />
              </button>

              <img 
                src={currentCategoryImages[expandedIndex].image?.startsWith('http') ? currentCategoryImages[expandedIndex].image : `${process.env.REACT_APP_AWS_URL}/${currentCategoryImages[expandedIndex].image}`} 
                alt="Expanded" 
                className="tw-max-w-[95vw] md:tw-max-w-[80vw] tw-max-h-[90vh] tw-object-contain tw-rounded-lg tw-shadow-2xl" 
              />

              {/* DESKTOP ONLY ARROWS: Pinned to Image edges */}
              {currentCategoryImages.length > 1 && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setExpandedIndex((prev) => (prev - 1 + currentCategoryImages.length) % currentCategoryImages.length); }}
                    className="tw-hidden md:tw-flex tw-absolute md:-tw-left-24 tw-top-1/2 tw--translate-y-1/2 tw-z-[100] tw-items-center tw-justify-center 
                    tw-w-16 tw-h-16 tw-rounded-full tw-bg-white/10 tw-backdrop-blur-md tw-border tw-border-white/20 
                    tw-text-white hover:tw-text-[#FF00A0] md:hover:tw-bg-white/20 tw-transition-all tw-cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="tw-text-4xl" />
                  </button>

                  <button 
                    onClick={(e) => { e.stopPropagation(); setExpandedIndex((prev) => (prev + 1) % currentCategoryImages.length); }}
                    className="tw-hidden md:tw-flex tw-absolute md:-tw-right-24 tw-top-1/2 tw--translate-y-1/2 tw-z-[100] tw-items-center tw-justify-center 
                    tw-w-16 tw-h-16 tw-rounded-full tw-bg-white/10 tw-backdrop-blur-md tw-border tw-border-white/20 
                    tw-text-white hover:tw-text-[#FF00A0] md:hover:tw-bg-white/20 tw-transition-all tw-cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="tw-text-4xl" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[1100] tw-flex tw-items-center tw-justify-center tw-bg-black/80 tw-backdrop-blur-sm">
          <div className="tw-bg-[#280D41] tw-border tw-border-[#5A3F49] tw-p-8 tw-rounded-2xl tw-max-w-sm tw-w-full tw-text-center">
            <h3 className="tw-text-white tw-text-xl tw-font-bold tw-mb-4">{t("Delete Photo?")}</h3>
            <p className="tw-text-[#E2BDC9] tw-text-sm tw-mb-8">
              {t("Are you sure you want to remove this photo? This will be finalized when you save your profile.")}
            </p>
            <div className="tw-flex tw-gap-4">
              <button 
                onClick={() => setDeleteModalOpen(false)}
                className="tw-flex-1 tw-py-2 tw-rounded-lg tw-bg-gray-600 tw-text-white tw-font-bold tw-border-none tw-cursor-pointer"
              >
                {t("Cancel")}
              </button>
              <button 
                onClick={confirmDelete}
                className="tw-flex-1 tw-py-2 tw-rounded-lg tw-bg-red-500 tw-text-white tw-font-bold tw-border-none tw-cursor-pointer"
              >
                {t("Delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}